import { useState, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';

const QUIZ_STORAGE_KEY = 'quizSubmissions';

export const useQuizHandlers = (
  questions,
  phone,
  quizId,
  quizName,
  totalQuizTimeInSeconds,
  timeLeft
) => {
  const [selectedAnswers, setSelectedAnswers] = useState(Array(questions.length).fill(null));
  const [markedQuestions, setMarkedQuestions] = useState(Array(questions.length).fill(false));
  const [hasSelectedOption, setHasSelectedOption] = useState(Array(questions.length).fill(false));
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [score, setScore] = useState(0);

  // Helper function to get all saved quiz submissions
  const getSavedSubmissions = useCallback(() => {
    try {
      const saved = localStorage.getItem(QUIZ_STORAGE_KEY);
      return saved ? JSON.parse(saved) : [];
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return [];
    }
  }, []);

  // Helper function to save a submission
  const saveSubmission = useCallback((submission) => {
    try {
      const submissions = getSavedSubmissions();
      submissions.push(submission);
      localStorage.setItem(QUIZ_STORAGE_KEY, JSON.stringify(submissions));
      return true;
    } catch (error) {
      console.error('Error saving to localStorage:', error);
      return false;
    }
  }, [getSavedSubmissions]);

  const calculateScore = useCallback(() => {
    let sum = 0;

    selectedAnswers.forEach((answer, index) => {
      if (answer === questions[index].answer) {
        sum += 4; // Correct answer
      } else if (answer && (answer === "option4" || answer === "option3" || answer === "option2" || answer === "option1")) {
        sum -= 1; // Wrong answer (selected but incorrect)
      }
      // No answer selected - sum remains unchanged
    });

    return sum;
  }, [selectedAnswers, questions]);

  const handleOptionChange = useCallback((event) => {
    const updatedAnswers = [...selectedAnswers];
    updatedAnswers[currentQuestionIndex] = event.target.value;
    setSelectedAnswers(updatedAnswers);

    const updatedSelectionStatus = [...hasSelectedOption];
    updatedSelectionStatus[currentQuestionIndex] = true;
    setHasSelectedOption(updatedSelectionStatus);

    // Auto-save progress to localStorage
    const autoSaveData = {
      quizId,
      phone,
      selectedAnswers: updatedAnswers,
      markedQuestions,
      currentQuestionIndex,
      lastSaved: new Date().toISOString()
    };
    localStorage.setItem(`quiz_${quizId}_${phone}_autosave`, JSON.stringify(autoSaveData));
  }, [currentQuestionIndex, hasSelectedOption, selectedAnswers, quizId, phone, markedQuestions]);

  const handleNextQuestion = useCallback(() => {
    setCurrentQuestionIndex((prev) => Math.min(prev + 1, questions.length - 1));
  }, [questions.length]);

  const handlePreviousQuestion = useCallback(() => {
    setCurrentQuestionIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  const handleQuestionButtonClick = useCallback((index) => {
    setCurrentQuestionIndex(index);
  }, []);

  const toggleMarkQuestion = useCallback(() => {
    const updatedMarks = [...markedQuestions];
    updatedMarks[currentQuestionIndex] = !updatedMarks[currentQuestionIndex];
    setMarkedQuestions(updatedMarks);
  }, [currentQuestionIndex, markedQuestions]);

  const handleSubmit = useCallback(async (isTimeUp = false) => {
    // if (!isTimeUp && !window.confirm("Are you sure you want to submit your answers and finish the exam?")) {
    //   return;
    // }

    const calculatedScore = calculateScore();
    setScore(calculatedScore);
    setIsSubmitted(true);

    const quizResults = {
      submissionId: uuidv4(),
      userId: phone,
      quizId: quizId,
      quizName: quizName,
      score: calculatedScore,
      totalQuestions: questions.length,
      answers: questions.map((question, index) => ({
        questionId: question._id.$oid || index + 1,
        questionText: question.question,
        userAnswer: selectedAnswers[index] || "Not answered",
        correctAnswer: question.answer,
        isCorrect: selectedAnswers[index] === question.answer,
      })),
     submittedAt: new Date().toISOString().split('T')[0],
      timeTaken: totalQuizTimeInSeconds - timeLeft,
      hasSelectedOption: hasSelectedOption,
    };

    // Save to localStorage instead of API
    const success = saveSubmission(quizResults);
    
    if (success) {
      console.log('Quiz results saved to localStorage successfully');
      
      // Clear auto-save data after successful submission
      localStorage.removeItem(`quiz_${quizId}_${phone}_autosave`);
    } else {
      console.error('Failed to save quiz results to localStorage');
    }
  }, [calculateScore, phone, quizId, quizName, questions, selectedAnswers, totalQuizTimeInSeconds, timeLeft, hasSelectedOption, saveSubmission]);

  const handleStartQuiz = useCallback(() => {
    setSelectedAnswers(Array(questions.length).fill(null));
    setMarkedQuestions(Array(questions.length).fill(false));
    setHasSelectedOption(Array(questions.length).fill(false));
    setCurrentQuestionIndex(0);
    setIsSubmitted(false);
    setScore(0);
  }, [questions.length]);

  // Function to load auto-saved progress
  const loadAutoSave = useCallback(() => {
    try {
      const autoSaveKey = `quiz_${quizId}_${phone}_autosave`;
      const saved = localStorage.getItem(autoSaveKey);
      if (saved) {
        const autoSaveData = JSON.parse(saved);
        setSelectedAnswers(autoSaveData.selectedAnswers || Array(questions.length).fill(null));
        setMarkedQuestions(autoSaveData.markedQuestions || Array(questions.length).fill(false));
        setCurrentQuestionIndex(autoSaveData.currentQuestionIndex || 0);
        
        // Update hasSelectedOption based on saved answers
        const selectionStatus = autoSaveData.selectedAnswers 
          ? autoSaveData.selectedAnswers.map(answer => answer !== null)
          : Array(questions.length).fill(false);
        setHasSelectedOption(selectionStatus);
        
        return true;
      }
      return false;
    } catch (error) {
      console.error('Error loading auto-save:', error);
      return false;
    }
  }, [quizId, phone, questions.length]);

  // Function to get all submissions for this user and quiz
  const getUserSubmissions = useCallback(() => {
    const submissions = getSavedSubmissions();
    return submissions.filter(sub => 
      sub.userId === phone && sub.quizId === quizId
    );
  }, [getSavedSubmissions, phone, quizId]);

  return {
    selectedAnswers,
    markedQuestions,
    hasSelectedOption,
    currentQuestionIndex,
    isSubmitted,
    score,
    handleOptionChange,
    handleNextQuestion,
    handlePreviousQuestion,
    handleQuestionButtonClick,
    toggleMarkQuestion,
    handleSubmit,
    handleStartQuiz,
    setCurrentQuestionIndex,
    setIsSubmitted,
    // New functions for localStorage management
    loadAutoSave,
    getUserSubmissions,
    getSavedSubmissions: getSavedSubmissions
  };
};